import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SearchResult } from '@/types';

// Google-Maps-style "Recent" search history, persisted locally so a returning
// visitor sees the places they previously picked. This is intentionally SEPARATE
// from searchStore.recentSearches (which other code owns): this store adds
// per-item removal + clear-all and is the source that powers the search box's
// "Recent" list. Persisted with zustand's persist middleware (same pattern as
// savedPlacesStore) under its own localStorage key.

// Keep the list short so the dropdown stays glanceable (Google shows a handful).
export const MAX_HISTORY = 8;

interface SearchHistoryState {
  // Most-recent-first, deduped by result id, capped at MAX_HISTORY.
  history: SearchResult[];

  // Record a picked/opened result. No-ops on results with no id or no
  // name/displayName so we never persist empty or fabricated rows. Moves an
  // already-present result back to the front (most-recent-first).
  addToHistory: (result: SearchResult) => void;
  // Remove a single entry by id (the per-item "x").
  removeFromHistory: (id: string) => void;
  // Clear the whole list (the "Clear all" affordance).
  clearHistory: () => void;
}

export const useSearchHistoryStore = create<SearchHistoryState>()(
  persist(
    (set) => ({
      history: [],

      addToHistory: (result) => {
        // Never record empty/failed picks: require a stable id and a label.
        if (!result || !result.id) return;
        if (!result.name && !result.displayName) return;

        set((state) => {
          const deduped = state.history.filter((r) => r.id !== result.id);
          return {
            history: [result, ...deduped].slice(0, MAX_HISTORY),
          };
        });
      },

      removeFromHistory: (id) =>
        set((state) => ({
          history: state.history.filter((r) => r.id !== id),
        })),

      clearHistory: () => set({ history: [] }),
    }),
    {
      name: 'tensormaps-search-history',
      // Only persist the list itself; actions are recreated on load.
      partialize: (state) => ({ history: state.history }),
    }
  )
);
